import { createContext, useContext } from 'react';
import { CreditsState, useCredits } from '../../../lib/use-credits';
import { PremiumPlan, usePremiumPlan } from './use-premium-plan';

// The Symphony settings surfaces (SettingsDesktop / SettingsMobile) show a single
// full-page loader while their primary APIs are in flight, rather than each card
// spinning on its own. To do that the loading signal has to live above the cards
// — but the credit/premium hooks each fetch on mount, so calling them in both the
// page gate AND the cards would double-fetch. This provider calls each hook ONCE
// and hands the result down; the cards read from context instead of re-fetching.
interface SettingsData {
  credits: CreditsState;
  plan: PremiumPlan;
}

const SettingsDataContext = createContext<SettingsData | null>(null);

export function SettingsDataProvider({ children }: { children: React.ReactNode }) {
  const credits = useCredits();
  const plan = usePremiumPlan();
  return <SettingsDataContext.Provider value={{ credits, plan }}>{children}</SettingsDataContext.Provider>;
}

export function useSettingsData(): SettingsData {
  const value = useContext(SettingsDataContext);
  if (!value) throw new Error('useSettingsData must be used within a SettingsDataProvider');
  return value;
}

// The page-level gate: true until BOTH primary calls (credits balance + premium
// plan) have settled. Individual cards still hide themselves when their own call
// failed / is unavailable, but no card renders its own inline spinner anymore.
export function useSettingsLoading(): boolean {
  const { credits, plan } = useSettingsData();
  return credits.loading || !plan.loaded;
}
